import { Check, Eye, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "../commissionEditQueue-formatters";
import type { RequestContext } from "../commissionEditQueue.types";
import { CommissionEditStatusBadge } from "./CommissionEditStatusBadge";

interface CommissionEditRequestGridProps {
  requests: RequestContext[];
  busyId: number | null;
  rejectingId: number | null;
  rejectNote: string;
  onSelect: (context: RequestContext) => void;
  onApprove: (requestId: number) => void;
  onReject: (requestId: number) => void;
  onStartReject: (requestId: number | null) => void;
  onRejectNote: (note: string) => void;
}

export function CommissionEditRequestGrid({
  requests,
  busyId,
  rejectingId,
  rejectNote,
  onSelect,
  onApprove,
  onReject,
  onStartReject,
  onRejectNote,
}: CommissionEditRequestGridProps) {
  if (requests.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucune demande ne correspond aux filtres.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
      {requests.map((context) => (
        <div key={context.request.id} className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-foreground">
                {context.typeLabel ?? context.transaction?.type ?? "Commission"} #
                {context.request.commissionTransactionId}
              </p>
              <p className="mt-1 text-[10.5px] text-muted-foreground">
                {context.requester?.fullName ?? `Agent #${context.request.requestedBy}`}
              </p>
            </div>
            <CommissionEditStatusBadge status={context.request.status} />
          </div>

          <p className="mt-3 line-clamp-2 min-h-[34px] rounded border border-border bg-surface-muted px-3 py-2 text-[11.5px] leading-relaxed text-body">
            {context.request.reason}
          </p>

          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 text-[11px]">
            <div>
              <p className="text-subtle">Demandée</p>
              <p className="mt-0.5 font-medium text-body">
                {formatDate(context.request.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-subtle">Champs</p>
              <p className="mt-0.5 font-medium tabular-nums text-body">
                {Object.keys(context.request.proposedChanges).length}
              </p>
            </div>
          </div>

          {rejectingId === context.request.id ? (
            <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
              <Input
                placeholder="Motif du refus (optionnel)"
                value={rejectNote}
                onChange={(event) => onRejectNote(event.target.value)}
                className="h-8 text-[12px]"
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => onStartReject(null)}>
                  Annuler
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onReject(context.request.id)}
                  disabled={busyId === context.request.id}
                >
                  Confirmer
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
              <Button size="sm" variant="ghost" onClick={() => onSelect(context)}>
                <Eye size={13} /> Détail
              </Button>
              {context.request.status === "pending" && (
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Refuser"
                    onClick={() => onStartReject(context.request.id)}
                    disabled={busyId === context.request.id}
                  >
                    <X size={13} />
                  </Button>
                  <Button
                    size="icon"
                    title="Approuver"
                    onClick={() => onApprove(context.request.id)}
                    disabled={busyId === context.request.id}
                  >
                    {busyId === context.request.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <Check size={13} />
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
